/**
 * Règles d'affichage PURES des cartes de défi live (Feed, onglet Live School) :
 * pastille d'état, « 3ème · Maths », « Démarre dans 3 min », bouton d'action.
 */

import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import {
  privateLiveFormatOf,
  privateLiveStage,
  type MyPrivateLive,
  type PrivateLiveSettings,
} from '@/lib/private-live';

/** Pastille en haut à gauche de la cover. */
export type LiveCardBadge = 'live' | 'private' | 'upcoming' | 'finished';

/** Ton du bouton d'action : bleu (rejoindre), or (organisateur), blanc, verre. */
export type LiveCardCtaTone = 'primary' | 'gold' | 'light' | 'glass';

const SHORT_SUBJECT: Record<string, string> = {
  math: 'Maths',
  'enseignement-scientifique': 'Ens. scientifique',
  'math-cpge': 'Maths prépa',
};

/** Nom court d'une matière (« Maths », « Maths Spécialité »…). PUR. */
export function shortSubjectLabel(subject: string | null | undefined): string | null {
  if (!subject) return null;
  return SHORT_SUBJECT[subject] ?? SUBJECTS.find((s) => s.value === subject)?.label ?? subject;
}

export function levelLabel(level: string): string {
  return SCHOOL_LEVELS.find((l) => l.value === level)?.label ?? level;
}

/** « 3ème · Maths », ou le niveau seul. PUR. */
export function levelSubjectChip(level: string, subject?: string | null): string {
  const s = shortSubjectLabel(subject);
  return s ? `${levelLabel(level)} · ${s}` : levelLabel(level);
}

/** « 1 participant », « 54 participants ». PUR. */
export function participantsLabel(n: number): string {
  return `${n} participant${n > 1 ? 's' : ''}`;
}

/**
 * Départ d'un live : « Démarre dans 3 min », « Démarre dans 1 h 20 »,
 * « Départ demain à 14 h 00 », « Départ sam. 25 oct. à 10 h 00 ». PUR.
 */
export function startsInLabel(startsAtMs: number, nowMs: number): string {
  const diff = startsAtMs - nowMs;
  if (diff <= 0) return 'C’est parti !';
  if (diff < 60_000) return 'Démarre dans moins d’une minute';
  const minutes = Math.ceil(diff / 60_000);
  if (minutes < 60) return `Démarre dans ${minutes} min`;
  if (minutes < 6 * 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `Démarre dans ${h} h ${String(m).padStart(2, '0')}` : `Démarre dans ${h} h`;
  }
  const at = new Date(startsAtMs);
  const now = new Date(nowMs);
  const hour = `${at.getHours()} h ${String(at.getMinutes()).padStart(2, '0')}`;
  const dayDiff = Math.round(
    (new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime() -
      new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) /
      86_400_000,
  );
  if (dayDiff === 0) return `Départ à ${hour}`;
  if (dayDiff === 1) return `Départ demain à ${hour}`;
  const day = at.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  return `Départ ${day} à ${hour}`;
}

/**
 * Format court d'un défi privé : « 7 questions · 1 avec brouillon »,
 * « 6 questions rapides », « 6 questions · 3 avec brouillon ». PUR.
 */
export function shortFormatLabel(
  settings: Partial<PrivateLiveSettings> | null | undefined,
  stepCount: number,
): string {
  const n = `${stepCount} question${stepCount > 1 ? 's' : ''}`;
  if (!settings) return n;
  const format = privateLiveFormatOf(settings);
  if (format === 'sheet') return `${n} · sur la feuille`;
  if (format === 'quick') {
    return settings.final_sheet
      ? `${n} · 1 avec brouillon`
      : `${n} rapide${stepCount > 1 ? 's' : ''}`;
  }
  if (format === 'alternate') {
    const sheets = Math.floor(stepCount / 2);
    return sheets > 0 ? `${n} · ${sheets} avec brouillon` : n;
  }
  return `${n} · brouillon possible`;
}

export interface PrivateLiveCardView {
  badge: LiveCardBadge;
  /** Ligne d'horaire (« Démarre dans 3 min », « En cours », « Terminé »). */
  timeLabel: string;
  /** « Philippe t'invite » / « Tu organises ce défi ». */
  intro: string;
  /** Format court (« 7 questions · 1 avec brouillon »). */
  summary: string;
  cta: { label: string; tone: LiveCardCtaTone };
  finished: boolean;
}

/** Contenu de la carte d'un défi privé (Feed, onglet Live School). PUR. */
export function privateLiveCardView(live: MyPrivateLive, nowMs: number): PrivateLiveCardView {
  const stage = privateLiveStage(live, nowMs);
  const host = live.host?.display_name?.split(' ')[0] ?? 'Un contact';
  const intro = live.hosted ? 'Tu organises ce défi' : `${host} t’invite`;
  const summary = shortFormatLabel(live.settings, live.step_count);
  if (stage === 'finished') {
    return {
      badge: 'finished',
      timeLabel: 'Défi terminé',
      intro,
      summary,
      cta: { label: 'Voir le classement', tone: 'glass' },
      finished: true,
    };
  }
  if (stage === 'running') {
    return {
      badge: 'live',
      timeLabel: 'En cours',
      intro,
      summary,
      cta: { label: live.hosted ? 'Ouvrir le défi' : 'Rejoindre le défi', tone: 'primary' },
      finished: false,
    };
  }
  const timeLabel =
    stage === 'awaiting'
      ? live.hosted
        ? 'À lancer quand tu veux'
        : `Départ quand ${host} lance`
      : startsInLabel(Date.parse(live.starts_at!), nowMs);
  return {
    badge: 'private',
    timeLabel,
    intro,
    summary,
    cta: live.hosted
      ? { label: 'Ouvrir le salon', tone: 'gold' }
      : live.joined
        ? { label: 'Retourner au salon', tone: 'light' }
        : { label: 'Rejoindre le salon', tone: 'light' },
    finished: false,
  };
}
